import React, { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { MarginProps, margin } from 'styled-system'
import { colors } from 'utils/colors'
import { WidthProps } from 'types/styled'

import { ErrorMessage } from 'components/TextInput'
import Spinner from 'components/Spinner'
import Icon from '../../Icon'
import Box from '../../Box'
import { CheckboxProps, CheckboxIconProps } from '../types'
import { StyledCheckbox } from '../IndeterminateCheckBox'
import useOnClickOutside from 'hooks/use-click-outside-hooks'
import InlineEditBtn from 'modules/Leads/LeadDetailV2/shared/InlineEditBtn'
import InlineInputButtons from 'components/Buttons/InlineInputButtons/v2'

export const CheckboxIcon = ({ checked, disabled, indeterminate, isError }: CheckboxIconProps) => (
  <IconContainer checked={checked || indeterminate} disabled={disabled} isError={isError}>
    {indeterminate ? (
      <Icon icon={['fas', 'minus']} color={colors.white} fontSize={10} />
    ) : checked ? (
      <Icon icon={['fas', 'check']} color={colors.white} fontSize={10} />
    ) : undefined}
  </IconContainer>
)

const EditableCheckbox = ({
  children,
  disabled,
  onChange,
  width,
  error,
  showError = true,
  isLoading = false,
  labelVerticalAlign = 'center',
  height,
  displayNode,
  saveOnClickOutside = true,
  checked = false,
  name,
  ...props
}: CheckboxProps) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLInputElement>(null)

  const [isEditBtnDisplay, setEditBtnDisplay] = useState(false)
  const [isEditClicked, setEditClicked] = useState(false)

  const [tempValue, setTempValue] = useState(checked)

  useOnClickOutside(ref, () => {
    if (!isEditClicked) {
      return
    }
    if (checked !== tempValue) {
      if (saveOnClickOutside) {
        onChange?.(tempValue)
      } else {
        setTempValue(checked)
      }
    }
    setEditBtnDisplay(false)
    setEditClicked(false)
  })

  const text = typeof children === 'string' || children instanceof String ? t(children as string) : children

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
  }

  const onEditChange = () => {
    setTempValue((prev) => !prev)
  }

  useEffect(() => {
    setTempValue(checked)
  }, [checked])

  if (displayNode && !isEditClicked) {
    return (
      <FieldBoxWrapper
        position="relative"
        onClick={() => setEditClicked(!disabled && true)}
        onMouseEnter={() => setEditBtnDisplay(true)}
        onMouseLeave={() => setEditBtnDisplay(false)}
      >
        {displayNode}
        {isEditBtnDisplay && !disabled && (
          <Box transform="translateY(-50%)" position="absolute" right={0} top="50%" backgroundColor="white">
            <InlineEditBtn />
          </Box>
        )}
      </FieldBoxWrapper>
    )
  }

  return (
    <Container ref={ref} onClick={handleClick} width={width} {...props}>
      <CheckboxLabel
        checked={!!tempValue}
        disabled={disabled}
        error={error}
        verticalAlign={labelVerticalAlign}
        height={height}
      >
        <CheckboxIcon checked={tempValue} disabled={disabled} isError={showError && !!error} />
        {text}
        <StyledCheckbox type="checkbox" checked={tempValue} name={name} disabled={disabled} onChange={onEditChange} />
      </CheckboxLabel>
      {showError && error && <ErrorMessage>{error}</ErrorMessage>}
      {isLoading && (
        <LoadingOverlay data-testid="checkbox-loading-overlay">
          <Spinner fontSize={16} />
        </LoadingOverlay>
      )}
      <Box>
        <InlineInputButtons
          position="relative"
          onClickCancel={() => {
            setEditBtnDisplay(false)
            setEditClicked(false)
            setTempValue(checked) // reset to prop value on cancel
          }}
          onClickConfirm={() => {
            onChange?.(tempValue)
            setEditBtnDisplay(false)
            setEditClicked(false)
          }}
        />
      </Box>
    </Container>
  )
}

const Container = styled.div<MarginProps & { width?: WidthProps; disabled?: boolean; inline?: boolean }>`
  ${margin}
  position: relative;
  display: ${(p) => (p.inline ? 'inline-block' : 'flex')};
  justify-content: space-between;
  text-align: left;
  padding: 6px 16px;
  ${(p) => p.width && (!p.inline ? `width: ${p.width === 'auto' ? '100%' : `${p.width}px`};` : '')}
`
const FieldBoxWrapper = styled(Box)<{ disabled?: boolean }>`
  *:hover {
    ${({ theme, disabled }) => disabled && `background: ${theme.colors.gray1};`}
  }
`

const CheckboxLabel = styled.label<{
  disabled?: boolean
  error?: string
  verticalAlign: string
  checked: boolean
  height?: string
}>`
  color: ${(p) =>
    p.error ? p.theme.colors.red : p.disabled && !p.checked ? p.theme.colors.gray4 : p.theme.colors.defaultText};
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  display: flex;
  align-items: ${(p) => p.verticalAlign};
  position: relative;
  min-height: ${(p) => p?.height ?? `1rem`};
  border: 1px solid transparent;
  user-select: text;
  ${(p) =>
    p.disabled &&
    css`
      div:first-child {
        cursor: not-allowed;
        pointer-events: none;
      }
    `}
  ${(p) =>
    !p.disabled &&
    css`
      &:hover {
        div:first-child {
          background: ${p.checked ? p.theme.colors.blue2 : p.theme.colors.gray4};
          border: 1px solid ${p.theme.colors.blue1};
        }
      }
    `}

  input {
    cursor: pointer;
    height: 0;
    width: 0;
    opacity: 0;
    position: absolute;
  }
`

const getBgColor = (p: { checked?: boolean; disabled?: boolean; isError?: boolean }) => {
  if (p.disabled) {
    return p.checked ? colors.blue1 : colors.gray4
  }

  if (p.isError) return colors.red

  return p.checked ? colors.blue1 : colors.gray3
}

const IconContainer = styled.div<{ checked?: boolean; disabled?: boolean; isError?: boolean }>`
  background: ${(p) => getBgColor(p)};
  border-radius: 3px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-right: 0.5rem;
  height: 16px;
  width: 16px;
  min-width: 16px;
  min-height: 16px;
  text-align: center;
`

const LoadingOverlay = styled.div`
  user-select: none;
  cursor: progress;
  box-sizing: border-box;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  padding: inherit;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`

export default EditableCheckbox
